import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Search, ExternalLink, UploadCloud, FileText, Trash2, Link2, Plus, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { TrainingAdmin } from "@/components/training/TrainingAdmin";
import { CompanyManager } from "@/components/training/CompanyManager";
import api from "@/lib/api";

const STATUSES = ["New", "Contacted", "Demo Scheduled", "Closed"];
const TYPES = ["Manufactured Housing", "Multifamily", "RV Resort", "Single-Family", "Mixed Portfolio"];
const PORTFOLIO_SIZES = ["1–250 units / pads", "251–500 units / pads", "501–1,500 units / pads", "1,500+ units / pads"];
const BOTTLENECKS = ["Onboarding & training staff", "Inspection consistency", "Compliance & fair housing", "Regional oversight", "All of the above"];
const LEAD_INITIAL = { full_name: "", email: "", phone: "", company: "", property_type: TYPES[0], portfolio_size: PORTFOLIO_SIZES[0], bottleneck: BOTTLENECKS[0] };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  New: "border-cadmium/50 bg-cadmium/10 text-sky-600",
  Contacted: "border-amber/50 bg-amber/10 text-amber-700",
  "Demo Scheduled": "border-moss/50 bg-moss/10 text-green-700",
  Closed: "border-white/20 bg-slate-100 text-muted-foreground",
};

const Metric = ({ label, value, testid, accent }) => (
  <div data-testid={testid} className="technical-card p-5">
    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    <p className={`mt-2 font-mono text-3xl font-semibold ${accent ? "text-sky-600" : "text-slate-900"}`}>{value}</p>
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [view, setView] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [certRows, setCertRows] = useState([]);
  const [caseRows, setCaseRows] = useState([]);
  const [quizRows, setQuizRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [stats, setStats] = useState({ total: 0, new: 0, demo_scheduled: 0, closed: 0, cert_waitlist: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [ptype, setPtype] = useState("");
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});
  const [showAddLead, setShowAddLead] = useState(false);
  const [leadForm, setLeadForm] = useState(LEAD_INITIAL);
  const [savingLead, setSavingLead] = useState(false);

  const createLead = async () => {
    setSavingLead(true);
    try {
      await api.post("/admin/leads", leadForm);
      toast.success(`Lead "${leadForm.full_name}" added`);
      setLeadForm(LEAD_INITIAL);
      setShowAddLead(false);
      fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add lead — check every field");
    } finally {
      setSavingLead(false);
    }
  };

  const deleteLead = async (id, name) => {
    if (!window.confirm(`Delete lead "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted");
      const statsRes = await api.get("/admin/stats");
      setStats(statsRes.data);
    } catch {
      toast.error("Delete failed");
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (ptype) params.property_type = ptype;
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/stats"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      const n = {};
      leadsRes.data.forEach((l) => { n[l.id] = l.notes || ""; });
      setNotes(n);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [q, status, ptype, navigate]);

  const fetchCert = useCallback(async () => {
    try {
      const res = await api.get("/admin/certification-interest");
      setCertRows(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load waitlist");
    }
  }, [navigate]);

  const fetchCases = useCallback(async () => {
    try {
      const res = await api.get("/admin/enforcement");
      setCaseRows(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load enforcement feed");
    }
  }, [navigate]);

  const fetchQuiz = useCallback(async () => {
    try {
      const res = await api.get("/admin/quiz-results");
      setQuizRows(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load quiz leads");
    }
  }, [navigate]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await api.get("/admin/assets");
      setAssets(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else toast.error("Failed to load assets");
    }
  }, [navigate]);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => {
        if (res.data?.role === "member") { navigate("/training"); return; }
        if (res.data?.role === "manager") { navigate("/company"); return; }
        setUser(res.data);
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  useEffect(() => {
    if (user) {
      setLoading(true);
      const t = setTimeout(fetchData, 250);
      return () => clearTimeout(t);
    }
  }, [user, fetchData]);

  useEffect(() => {
    if (user && view === "cert") fetchCert();
    if (user && view === "cases") fetchCases();
    if (user && view === "assets") fetchAssets();
    if (user && view === "quiz") fetchQuiz();
  }, [user, view, fetchCert, fetchCases, fetchAssets, fetchQuiz]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status: newStatus });
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status: newStatus } : l)));
      const statsRes = await api.get("/admin/stats");
      setStats(statsRes.data);
      toast.success(`Lead marked ${newStatus}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const saveNotes = async (id) => {
    try {
      await api.patch(`/admin/leads/${id}`, { notes: notes[id] ?? "" });
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
  };

  const toggleCase = async (id, approved) => {
    try {
      await api.patch(`/admin/enforcement/${id}`, { approved });
      setCaseRows((prev) => prev.map((c) => (c.id === id ? { ...c, approved } : c)));
      toast.success(approved ? "Case published to ticker" : "Case removed from ticker");
    } catch {
      toast.error("Could not update case");
    }
  };

  const uploadAsset = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/assets", fd);
      toast.success("Asset uploaded");
      fetchAssets();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteAsset = async (id) => {
    try {
      await api.delete(`/admin/assets/${id}`);
      setAssets((prev) => prev.filter((a) => a.id !== id));
      toast.success("Asset deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyLink = (path) => {
    navigator.clipboard.writeText(`${BACKEND}/api/files/${path}`);
    toast.success("Link copied");
  };

  const exportCsv = () => {
    const header = ["Name", "Email", "Company", "Property Type", "Portfolio Size", "Bottleneck", "Status", "Created", "Notes"];
    const rows = leads.map((l) => [l.full_name, l.email, l.company, l.property_type, l.portfolio_size, l.bottleneck, l.status, l.created_at, (l.notes || "").replace(/\n/g, " ")]);
    const csv = [header, ...rows].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `opera360-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/login");
  };

  const selectCls = "rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-900 focus:outline-none focus:border-cadmium/60";
  const tabCls = (active) =>
    `rounded-md px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors ${
      active ? "border-cadmium/60 bg-cadmium/10 text-sky-600" : "border-slate-200 text-muted-foreground hover:text-slate-900 hover:border-white/25"
    }`;

  return (
    <div className="min-h-screen workspace-light bg-[#EDF4FB]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-[#EDF4FB]/90 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" aria-label="OPERA360 home"><LogoMark size={26} /></Link>
            <span className="font-cabinet font-black tracking-tight text-lg text-slate-900">
              OPERA<span className="text-sky-600">360</span>
            </span>
            <span className="ml-2 rounded-sm border border-cadmium/40 bg-cadmium/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-sky-600">
              Admin CRM
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/portal" data-testid="admin-portal-link" className="text-xs font-mono uppercase tracking-widest text-green-700 hover:text-white transition-colors">
              Portal →
            </Link>
            <span className="hidden sm:block text-xs font-mono text-muted-foreground">{user?.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:border-cadmium/50 hover:text-sky-600 transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          <button data-testid="admin-tab-leads" onClick={() => setView("leads")} className={tabCls(view === "leads")}>
            Demo Leads ({stats.total})
          </button>
          <button data-testid="admin-tab-cert" onClick={() => setView("cert")} className={tabCls(view === "cert")}>
            Certification Waitlist ({stats.cert_waitlist ?? 0})
          </button>
          <button data-testid="admin-tab-cases" onClick={() => setView("cases")} className={tabCls(view === "cases")}>
            Enforcement Feed
          </button>
          <button data-testid="admin-tab-quiz" onClick={() => setView("quiz")} className={tabCls(view === "quiz")}>
            Quiz Leads ({stats.quiz_takers ?? 0})
          </button>
          <button data-testid="admin-tab-assets" onClick={() => setView("assets")} className={tabCls(view === "assets")}>
            Asset Library
          </button>
          <button data-testid="admin-tab-training" onClick={() => setView("training")} className={tabCls(view === "training")}>
            Training
          </button>
          <button data-testid="admin-tab-companies" onClick={() => setView("companies")} className={tabCls(view === "companies")}>
            Companies
          </button>
        </div>

        {view === "companies" && <CompanyManager />}

        {view === "leads" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric label="Total Leads" value={stats.total} testid="admin-metric-total" />
              <Metric label="New" value={stats.new} testid="admin-metric-new" accent />
              <Metric label="Demos Scheduled" value={stats.demo_scheduled} testid="admin-metric-demos" />
              <Metric label="Closed" value={stats.closed} testid="admin-metric-closed" />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="admin-search-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, company…"
                  className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60"
                />
              </div>
              <select data-testid="admin-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
                <option value="">All statuses</option>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select data-testid="admin-type-filter" value={ptype} onChange={(e) => setPtype(e.target.value)} className={selectCls}>
                <option value="">All property types</option>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <button
                data-testid="admin-refresh-btn"
                onClick={fetchData}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3.5 py-2 text-xs font-mono uppercase tracking-wider text-muted-foreground hover:border-cadmium/50 hover:text-sky-600 transition-colors"
              >
                <RefreshCw size={13} /> Refresh
              </button>
              <button
                data-testid="admin-export-csv-btn"
                onClick={exportCsv}
                className="inline-flex items-center gap-1.5 rounded-md bg-cadmium px-3.5 py-2 text-xs font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors"
              >
                <Download size={13} /> Export CSV
              </button>
              <button
                data-testid="admin-add-lead-btn"
                onClick={() => setShowAddLead(!showAddLead)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${showAddLead ? "bg-moss text-white" : "border border-moss/50 text-green-700 hover:bg-moss/10"}`}
              >
                <Plus size={13} /> Add Lead
              </button>
            </div>

            {showAddLead && (
              <div className="technical-card p-5 mb-4" data-testid="admin-add-lead-form">
                <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-4">Add a lead manually</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                  <input data-testid="add-lead-name" value={leadForm.full_name} onChange={(e) => setLeadForm({ ...leadForm, full_name: e.target.value })} placeholder="Full name *" className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60" />
                  <input data-testid="add-lead-email" type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} placeholder="Email *" className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60" />
                  <input data-testid="add-lead-phone" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} placeholder="Phone *" className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60" />
                  <input data-testid="add-lead-company" value={leadForm.company} onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })} placeholder="Company *" className="rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-muted-foreground/50 focus:outline-none focus:border-cadmium/60" />
                  <select data-testid="add-lead-type" value={leadForm.property_type} onChange={(e) => setLeadForm({ ...leadForm, property_type: e.target.value })} className={selectCls}>
                    {TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <select data-testid="add-lead-size" value={leadForm.portfolio_size} onChange={(e) => setLeadForm({ ...leadForm, portfolio_size: e.target.value })} className={selectCls}>
                    {PORTFOLIO_SIZES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <select data-testid="add-lead-bottleneck" value={leadForm.bottleneck} onChange={(e) => setLeadForm({ ...leadForm, bottleneck: e.target.value })} className={`${selectCls} sm:col-span-2`}>
                    {BOTTLENECKS.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="flex gap-3">
                  <button data-testid="add-lead-save" onClick={createLead} disabled={savingLead} className="inline-flex items-center gap-2 rounded-md bg-moss px-4 py-2 text-xs font-mono uppercase tracking-widest text-white hover:opacity-90 transition-opacity disabled:opacity-60">
                    {savingLead ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Save Lead
                  </button>
                  <button data-testid="add-lead-cancel" onClick={() => setShowAddLead(false)} className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-slate-900 transition-colors">Cancel</button>
                </div>
              </div>
            )}

            <div className="technical-card overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[900px]" data-testid="admin-leads-table">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="px-5 py-3.5">Lead</th>
                    <th className="px-5 py-3.5">Portfolio</th>
                    <th className="px-5 py-3.5">Bottleneck</th>
                    <th className="px-5 py-3.5">Created</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-white/[0.06] hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="font-semibold text-slate-900">{l.full_name}</p>
                        <p className="text-xs text-muted-foreground">{l.email}</p>
                        {l.phone && <p className="text-xs font-mono text-cadmium/90">{l.phone}</p>}
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-slate-900/85">{l.company}</p>
                        <p className="text-xs text-muted-foreground">{l.property_type} · {l.portfolio_size}</p>
                      </td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{l.bottleneck}</td>
                      <td className="px-5 py-4 text-xs font-mono text-muted-foreground">
                        {new Date(l.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4">
                        <select
                          data-testid={`admin-lead-status-${l.id}`}
                          value={l.status}
                          onChange={(e) => updateStatus(l.id, e.target.value)}
                          className={`rounded-sm border px-2 py-1.5 text-[11px] font-mono font-semibold focus:outline-none cursor-pointer ${STATUS_STYLE[l.status]}`}
                        >
                          {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`admin-lead-notes-${l.id}`}
                            value={notes[l.id] ?? ""}
                            onChange={(e) => setNotes({ ...notes, [l.id]: e.target.value })}
                            placeholder="Add note…"
                            className="w-40 rounded-sm border border-slate-200 bg-[#EDF4FB] px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-muted-foreground/40 focus:outline-none focus:border-cadmium/60"
                          />
                          <button
                            data-testid={`admin-lead-notes-save-${l.id}`}
                            onClick={() => saveNotes(l.id)}
                            className="rounded-sm border border-slate-200 px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:border-moss/50 hover:text-green-700 transition-colors"
                          >
                            Save
                          </button>
                          <button
                            data-testid={`admin-lead-delete-${l.id}`}
                            onClick={() => deleteLead(l.id, l.full_name)}
                            title="Delete lead"
                            className="rounded-sm border border-slate-200 px-2 py-1.5 text-muted-foreground hover:border-red-400/50 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && leads.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-5 py-14 text-center" data-testid="admin-leads-empty">
                        <p className="text-sm text-muted-foreground">No leads yet.</p>
                        <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">
                          Submissions from the Book a Demo form appear here instantly.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "cert" && (
          <div className="technical-card overflow-x-auto">
            <table className="w-full text-left text-sm min-w-[700px]" data-testid="admin-cert-table">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                  <th className="px-5 py-3.5">Name</th>
                  <th className="px-5 py-3.5">Email</th>
                  <th className="px-5 py-3.5">Company</th>
                  <th className="px-5 py-3.5">Role</th>
                  <th className="px-5 py-3.5">Joined</th>
                </tr>
              </thead>
              <tbody>
                {certRows.map((r) => (
                  <tr key={r.id} data-testid={`admin-cert-row-${r.id}`} className="border-b border-white/[0.06] hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4 font-semibold text-slate-900">{r.full_name}</td>
                    <td className="px-5 py-4 text-xs text-muted-foreground">{r.email}</td>
                    <td className="px-5 py-4">
                      <p className="text-xs text-slate-900/85">{r.company || "—"}</p>
                      {(r.phone || r.portfolio_size) && (
                        <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                          {[r.phone, r.portfolio_size].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className="rounded-sm border border-cadmium/40 bg-cadmium/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-sky-600">
                        {r.role || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-xs font-mono text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {certRows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-14 text-center" data-testid="admin-cert-empty">
                      <p className="text-sm text-muted-foreground">No waitlist signups yet.</p>
                      <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">
                        Certification section submissions appear here.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {view === "cases" && (
          <>
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground/70 mb-4">
              The monthly cron pulls new DOJ fair-housing releases here as Pending — approve to publish them to the landing-page ticker.
            </p>
            <div className="technical-card overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[800px]" data-testid="admin-cases-table">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="px-5 py-3.5">Date</th>
                    <th className="px-5 py-3.5">Case</th>
                    <th className="px-5 py-3.5">Amount</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {caseRows.map((c) => (
                    <tr key={c.id} data-testid={`admin-case-row-${c.id}`} className="border-b border-white/[0.06] hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4 text-xs font-mono text-muted-foreground whitespace-nowrap">{c.date_label}</td>
                      <td className="px-5 py-4">
                        <a href={c.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-slate-900/85 hover:text-sky-600 transition-colors text-xs leading-relaxed">
                          {c.title} <ExternalLink size={11} className="shrink-0" />
                        </a>
                      </td>
                      <td className="px-5 py-4 font-mono text-sm text-slate-900 whitespace-nowrap">{c.amount || "—"}</td>
                      <td className="px-5 py-4">
                        <span className={`rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${
                          c.approved ? "border-moss/50 bg-moss/10 text-green-700" : "border-amber/50 bg-amber/10 text-amber-700"
                        }`}>
                          {c.approved ? "Live on ticker" : "Pending review"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <button
                          data-testid={`admin-case-toggle-${c.id}`}
                          onClick={() => toggleCase(c.id, !c.approved)}
                          className={`rounded-sm border px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors ${
                            c.approved
                              ? "border-slate-300 text-muted-foreground hover:border-red-500/50 hover:text-red-600"
                              : "border-moss/40 text-green-700 hover:bg-moss/10"
                          }`}
                        >
                          {c.approved ? "Unpublish" : "Approve"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {caseRows.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-14 text-center" data-testid="admin-cases-empty">
                        <p className="text-sm text-muted-foreground">No enforcement items yet.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "quiz" && (
          <>
            <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground/70 mb-4">
              Everyone who emailed themselves a quiz score — warm leads for cohort one. Converted = they also joined the waitlist or booked a demo.
            </p>
            <div className="technical-card overflow-x-auto">
              <table className="w-full text-left text-sm min-w-[600px]" data-testid="admin-quiz-table">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">
                    <th className="px-5 py-3.5">Email</th>
                    <th className="px-5 py-3.5">Score</th>
                    <th className="px-5 py-3.5">Taken</th>
                    <th className="px-5 py-3.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {quizRows.map((r) => (
                    <tr key={r.id} data-testid={`admin-quiz-row-${r.id}`} className="border-b border-white/[0.06] hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4 text-sm text-slate-900/85">{r.email}</td>
                      <td className="px-5 py-4">
                        <span className={`font-mono text-lg font-semibold ${r.score >= 4 ? "text-green-700" : r.score >= 3 ? "text-amber-700" : "text-sky-600"}`}>
                          {r.score}/5
                        </span>
                      </td>
                      <td className="px-5 py-4 text-xs font-mono text-muted-foreground">
                        {new Date(r.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`rounded-sm border px-2 py-1 text-[10px] font-mono uppercase tracking-wider ${
                          r.converted ? "border-moss/50 bg-moss/10 text-green-700" : "border-slate-300 text-muted-foreground"
                        }`}>
                          {r.converted ? "Converted" : "New prospect"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {quizRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-14 text-center" data-testid="admin-quiz-empty">
                        <p className="text-sm text-muted-foreground">No quiz takers yet.</p>
                        <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">
                          Emails collected on the certification quiz appear here.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {view === "assets" && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground/70">
                Images, PDFs, and documents stored in your media library — served through your own backend.
              </p>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,text/plain,text/csv"
                  onChange={uploadAsset}
                  className="hidden"
                  data-testid="admin-asset-file-input"
                />
                <button
                  data-testid="admin-asset-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 rounded-md bg-cadmium px-4 py-2.5 text-xs font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60"
                >
                  <UploadCloud size={14} /> {uploading ? "Uploading…" : "Upload File"}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-assets-grid">
              {assets.map((a) => (
                <div key={a.id} data-testid={`admin-asset-card-${a.id}`} className="technical-card p-0 overflow-hidden group">
                  <div className="h-36 bg-[#EDF4FB] flex items-center justify-center overflow-hidden border-b border-slate-200">
                    {a.content_type?.startsWith("image/") ? (
                      <img src={`${BACKEND}/api/files/${a.storage_path}`} alt={a.original_filename} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <FileText size={28} className="text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="p-3.5">
                    <p className="text-xs font-semibold text-slate-900 truncate" title={a.original_filename}>{a.original_filename}</p>
                    <p className="mt-0.5 text-[10px] font-mono text-muted-foreground">
                      {(a.size / 1024).toFixed(0)} KB · {new Date(a.created_at).toLocaleDateString()}
                    </p>
                    <div className="mt-3 flex items-center gap-1.5">
                      <button
                        data-testid={`admin-asset-copy-${a.id}`}
                        onClick={() => copyLink(a.storage_path)}
                        className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:border-cadmium/50 hover:text-sky-600 transition-colors"
                      >
                        <Link2 size={10} /> Link
                      </button>
                      <button
                        data-testid={`admin-asset-delete-${a.id}`}
                        onClick={() => deleteAsset(a.id)}
                        className="inline-flex items-center gap-1 rounded-sm border border-slate-200 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:border-red-500/50 hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={10} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {assets.length === 0 && (
                <div className="col-span-full technical-card p-14 text-center" data-testid="admin-assets-empty">
                  <UploadCloud size={26} className="mx-auto text-muted-foreground/40 mb-3" />
                  <p className="text-sm text-muted-foreground">No assets yet.</p>
                  <p className="mt-1 text-xs font-mono uppercase tracking-wider text-muted-foreground/60">
                    Upload logos, SOPs, or property photos — 10MB max per file.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {view === "training" && <TrainingAdmin />}
      </main>
    </div>
  );
}
